import React, { Component } from 'react';
import {
    Box,
    Button,
    TextField,
    Typography,
} from '@mui/material';

class PaginationWrapper extends Component {
    constructor(props) {
        super(props);
        this.state = {
            search: '',
            page: 1,
        };
    }

    normalize = (s) => (s ?? '').toString().toLowerCase().trim();

    setSearch = (event) => {
        const text = event.target.value;
        this.setState({ search: text, page: 1 }, this.emit);
    };

    goPrev = () => {
        this.setState(
            (prev) => ({ page: Math.max(1, prev.page - 1) }),
            this.emit
        );
    };

    goNext = (totalPages) => {
        this.setState(
            (prev) => ({ page: Math.min(totalPages, prev.page + 1) }),
            this.emit
        );
    };

    compute = () => {
        const {
            items,
            pageSize = 10,
            threshold = 10,
            filterFn,
            disablePaginationOnSearch = true,
        } = this.props;

        const list = Array.isArray(items) ? items : [];
        const term = this.normalize(this.state.search);
        const hasSearch = term.length > 0;

        const filtered =
            hasSearch && typeof filterFn === 'function'
                ? list.filter((it) => filterFn(it, term))
                : list;

        const shouldPaginateByCount = filtered.length > threshold;
        const paginate = shouldPaginateByCount && !(disablePaginationOnSearch && hasSearch);

        if (!paginate) {
            return {
                search: this.state.search,
                hasSearch,
                filteredCount: filtered.length,
                pagedItems: filtered,
                totalPages: 1,
                safePage: 1,
                showPagination: false,
            };
        }

        const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
        const safePage = Math.min(Math.max(1, this.state.page), totalPages);
        const start = (safePage - 1) * pageSize;
        const pagedItems = filtered.slice(start, start + pageSize);

        return {
            search: this.state.search,
            hasSearch,
            filteredCount: filtered.length,
            pagedItems,
            totalPages,
            safePage,
            showPagination: true,
        };
    };

    emit = () => {
        const computed = this.compute();
        this.props.onPageItemsChange?.(computed.pagedItems, computed);
    };

    componentDidMount() {
        this.emit();
    }

    componentDidUpdate(prevProps, prevState) {
        if (
            prevProps.items !== this.props.items ||
            prevProps.pageSize !== this.props.pageSize ||
            prevProps.threshold !== this.props.threshold ||
            prevState.search !== this.state.search ||
            prevState.page !== this.state.page
        ) {
            this.emit();
        }
    }

    render() {
        const {
            items,
            children,
            showSearch = true,
            searchLabel,
            style,
        } = this.props;

        const computed = this.compute();
        const listCount = Array.isArray(items) ? items.length : 0;
        const showSearchInput = showSearch && listCount > 0;

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, ...style }}>
                {showSearchInput && (
                    <TextField
                        fullWidth
                        value={computed.search}
                        onChange={this.setSearch}
                        placeholder={searchLabel ?? 'Suchen'}
                        size="small"
                    />
                )}

                {typeof children === 'function'
                    ? children(computed.pagedItems, computed)
                    : children}

                {computed.showPagination && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 2,
                        }}
                    >
                        <Button
                            variant="contained"
                            onClick={this.goPrev}
                            disabled={computed.safePage <= 1}
                        >
                            Zurück
                        </Button>

                        <Typography>
                            {computed.safePage} / {computed.totalPages}
                        </Typography>

                        <Button
                            variant="contained"
                            onClick={() => this.goNext(computed.totalPages)}
                            disabled={computed.safePage >= computed.totalPages}
                        >
                            Weiter
                        </Button>
                    </Box>
                )}
            </Box>
        );
    }
}

export default PaginationWrapper;